// The preference center's choices and their pure rules (Marketing Hub row
// 3.5, drawing screen 53, plan section 5.4).
//
// CONSUMER: both (the public preference page draws the choices; the save in
// shared/db/marketing-preferences.ts turns them into column writes; the
// household Marketing tab prints what a person chose).
//
// ONE HOME PER FACT. A channel the OS already records keeps its column:
// Email is contacts.marketing_ok, Text is contacts.sms_opt_in with
// contacts.tcpa_consent_at. Mail, topics and cadence are new and live in
// contact_marketing_preferences. "Only the company I asked" is the affiliate
// opt-out on contacts, the column the affiliate rule reads.
//
// Hard rule: PURE. No database, no React.

import { MARKETING_TOPICS, isMarketingTopicKey } from "./topics";

export const CADENCES = [
  { key: "monthly", label: "Monthly is plenty" },
  { key: "when_meaningful", label: "Only when something changes" },
  { key: "more_education", label: "More education, please" },
  { key: "only_what_i_use", label: "Only about what I already use Stewardship for" },
] as const;
export type Cadence = (typeof CADENCES)[number]["key"];

export function isCadence(value: unknown): value is Cadence {
  return typeof value === "string" && CADENCES.some((c) => c.key === value);
}

export const AFFILIATE_CHOICES = [
  { key: "one_team", label: "Yes, one team" },
  { key: "only_company_asked", label: "Only the company I asked" },
] as const;
export type AffiliateChoice = (typeof AFFILIATE_CHOICES)[number]["key"];

export function isAffiliateChoice(value: unknown): value is AffiliateChoice {
  return value === "one_team" || value === "only_company_asked";
}

/** FCRA section 624: a marketing opt-out lasts at least five years. The OS
 *  records when that period ends so the renewal notice can be offered; it
 *  never lapses on its own (the affiliate rule treats an expired opt-out as
 *  still standing until the person chooses again). */
export const AFFILIATE_OPT_OUT_YEARS = 5;

export function optOutExpiry(nowIso: string): string {
  const d = new Date(nowIso);
  return new Date(Date.UTC(d.getUTCFullYear() + AFFILIATE_OPT_OUT_YEARS, d.getUTCMonth(), d.getUTCDate())).toISOString();
}

/**
 * The words shown next to "Text" and recorded with the tap. A text channel
 * needs the person's written consent (TCPA); the tap on this page, with
 * these words in front of them and their number named, is that consent.
 * The version travels into the audit row so the words they saw can be read
 * back later; change the words, bump the version.
 */
export const TEXT_CONSENT_VERSION = "2026-10-07";
export function textConsentWords(phoneLastFour: string): string {
  return `By choosing Text, you agree that Stewardship may send you marketing text messages at the number ending ${phoneLastFour}. Agreeing is not a condition of buying anything. Message and data rates may apply. Reply STOP at any time to stop.`;
}

/** What the person sees, per person. Never another person's choices. */
export interface PreferenceView {
  first_name: string;
  /** The companies serving this household, for the footer line. */
  company_names: string[];
  topics: string[];
  cadence: Cadence | null;
  email: boolean;
  /** Why email cannot be turned back on here, when it cannot. */
  email_blocked: "no_address" | "bounced" | "do_not_email" | null;
  text: boolean;
  /** Last four of the mobile number on file; null when none, and Text is off. */
  phone_last_four: string | null;
  /** Row 8.13: when they asked us to stop texting; null when they have not. */
  text_stopped_at: string | null;
  mail: boolean | null;
  affiliate: AffiliateChoice;
  /** When the person (or an advisor) last saved here; null: never. */
  saved_at: string | null;
  /** The companies this person stopped marketing email from, one by one
   *  (row 6.3's unsubscribe page), each turnable back on here (slice 6
   *  audit M4). */
  company_opt_outs: { slug: string; name: string }[];
}

export interface PreferenceSave {
  topics: string[];
  cadence: Cadence | null;
  email: boolean;
  text: boolean;
  /** The consent words' version the page showed; required to turn Text on. */
  text_consent_version: string | null;
  mail: boolean;
  affiliate: AffiliateChoice;
}

export type ParsedSave = { ok: true; save: PreferenceSave } | { ok: false; message: string };

/** Parse a save from the page. Unknown topic keys are refused, not dropped,
 *  so a stale page never silently loses a choice. */
export function parsePreferenceSave(body: unknown): ParsedSave {
  const b = (body ?? {}) as Record<string, unknown>;
  const topics = Array.isArray(b.topics) ? b.topics : [];
  if (!topics.every(isMarketingTopicKey)) return { ok: false, message: "One of those topics is not on the list. Reload the page and try again." };
  if (b.cadence !== null && b.cadence !== undefined && !isCadence(b.cadence)) return { ok: false, message: "Pick how often from the list." };
  if (typeof b.email !== "boolean" || typeof b.text !== "boolean" || typeof b.mail !== "boolean") {
    return { ok: false, message: "Choose your channels and save again." };
  }
  if (!isAffiliateChoice(b.affiliate)) return { ok: false, message: "Choose how the five companies may reach you." };
  if (b.text && b.text_consent_version !== TEXT_CONSENT_VERSION) {
    return { ok: false, message: "Read the words next to Text and tap it again to agree." };
  }
  return {
    ok: true,
    save: {
      // De-duplicated and in the list's own order, so a save is comparable.
      topics: MARKETING_TOPICS.map((t) => t.key).filter((k) => (topics as string[]).includes(k)),
      cadence: (b.cadence as Cadence | null | undefined) ?? null,
      email: b.email,
      text: b.text,
      text_consent_version: b.text ? TEXT_CONSENT_VERSION : null,
      mail: b.mail,
      affiliate: b.affiliate,
    },
  };
}

/** Is this person's affiliate marketing opt-out standing on `todayIso`?
 *  An expired opt-out still stands: the OS offers the renewal notice, it
 *  never resumes cross-company marketing on its own. */
export function affiliateOptOutStanding(optOutAt: string | null): boolean {
  return optOutAt !== null && optOutAt.trim() !== "";
}

/** One line for the household Marketing tab: "Maria: Owning a home, Taxes ·
 *  monthly · only the company she asked". */
export function preferenceSummary(p: {
  topics: readonly string[];
  cadence: Cadence | null;
  mail: boolean | null;
  affiliateOptOut: boolean;
  /** False when only an affiliate record exists: topics were never chosen. */
  chosen?: boolean;
}): string {
  const parts: string[] = [];
  if (p.chosen === false) return p.affiliateOptOut ? "only the company asked · topics not chosen" : "one team · topics not chosen";
  const labels = MARKETING_TOPICS.filter((t) => p.topics.includes(t.key)).map((t) => t.label);
  parts.push(labels.length > 0 ? labels.join(", ") : "no topics chosen");
  const cadence = CADENCES.find((c) => c.key === p.cadence);
  if (cadence) parts.push(cadence.label.toLowerCase());
  if (p.mail === true) parts.push("mail yes");
  if (p.mail === false) parts.push("no mail");
  parts.push(p.affiliateOptOut ? "only the company asked" : "one team");
  return parts.join(" · ");
}

const MONTH_WORDS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mar 2026" from an ISO timestamp or date; null when it will not parse. */
export function monthYearWords(iso: string | null): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return `${MONTH_WORDS[month - 1]} ${m[1]}`;
}

/**
 * The household Marketing tab's "Affiliate notice" line for ONE person
 * (drawing screen 3: "delivered v2, Mar 2026 · no opt-out"). Per person,
 * never per household (modal 30): a spouse who is not on the contract has
 * no notice of their own. Says "no notice on file" plainly when none was
 * recorded, which on 2026-10-07 is every person in production (gate 15):
 * the line is honest about that rather than blank.
 */
export function affiliateNoticeLine(p: {
  notice: { version: string | null; delivered_at: string | null } | null;
  affiliate_opt_out: boolean;
  disclosure: { kind: "affiliated_business_disclosure" | "form_adv"; signed_at: string | null } | null;
}): string {
  const parts: string[] = [];
  if (p.notice?.delivered_at) {
    const when = monthYearWords(p.notice.delivered_at);
    parts.push(`delivered${p.notice.version ? ` ${p.notice.version}` : ""}${when ? `, ${when}` : ""}`);
  } else {
    parts.push("no notice on file");
  }
  parts.push(p.affiliate_opt_out ? "opted out" : "no opt-out");
  if (p.disclosure) {
    const word = p.disclosure.kind === "form_adv" ? "Form ADV" : "affiliated business disclosure";
    const when = monthYearWords(p.disclosure.signed_at);
    parts.push(`${word}${when ? ` signed ${when}` : " on file"}`);
  }
  return parts.join(" · ");
}

export const DISCLOSURE_WORD: Record<"affiliated_business_disclosure" | "form_adv", string> = {
  affiliated_business_disclosure: "Affiliated business disclosure (Mortgage, Realty)",
  form_adv: "Form ADV (Financial)",
};

/** The privacy notice log's strip (screen 65). */
export interface PrivacyNoticeLog {
  /** Households with active relationships in two or more companies. */
  multi_company_households: number;
  /** Of those, households where every adult has the given version. Keyed by
   *  version; "none" counts households where nobody has a notice on file. */
  by_version: { version: string; households: number }[];
  households_without_notice: number;
  /** People with a standing affiliate marketing opt-out. */
  marketing_opt_outs: number;
  sharing_opt_outs: number;
  /** People with a disclosure on file, by kind. */
  disclosures: { kind: "affiliated_business_disclosure" | "form_adv"; people: number }[];
  recent: {
    household_id: string;
    household_name: string;
    people: { first_name: string; version: string | null; delivered_at: string | null; marketing_opt_out_at: string | null }[];
  }[];
  /** COUNT(*) over the same WHERE as `recent`, never its length. */
  recent_total: number;
}
